import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress, Paper, Divider, Alert } from '@mui/material';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';

// XSS 방지용 라이브러리(권장): npm install dompurify
import DOMPurify from 'dompurify';
import CaseBriefForm from './CaseBriefForm';

const CaseBriefDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const { user } = useAuth();

  const fetchBrief = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('case_brief')
      .select('*')
      .eq('id', id)
      .single();
    if (!error) setBrief(data);
    setLoading(false);
  };

  useEffect(() => {
    const incrementViews = async () => {
      if (!id) return;
      const { error } = await supabase.rpc('increment_views', { row_id: id });
      if (error) {
        console.error('조회수 증가 실패:', error);
      } else {
        console.log('조회수 증가 성공');
      }
    };
    incrementViews();
    fetchBrief();
  }, [id]);

  const handleEditSuccess = () => {
    setEditOpen(false);
    fetchBrief();
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  if (!brief) return <Typography>해당 케이스 브리프를 찾을 수 없습니다.</Typography>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          목록으로
        </Button>
        {!user && (
          <Alert severity="info" sx={{ mb: 2 }}>
            검색과 우클릭 활성화는 로그인 상태에서만 허용됩니다.
          </Alert>
        )}
        <Paper 
          sx={{ 
            p: 3,
            // 로그인하지 않은 사용자는 텍스트 선택 불가능
            userSelect: user ? 'auto' : 'none',
            WebkitUserSelect: user ? 'auto' : 'none',
            MozUserSelect: user ? 'auto' : 'none',
            msUserSelect: user ? 'auto' : 'none',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {/* 왼쪽: content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" gutterBottom>{brief.title}</Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {brief.author_name} | {new Date(brief.created_at).toLocaleString()}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ mt: 1 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(brief.content) }}
              />
            </Box>
            {/* 오른쪽: kor_summary, kor_content */}
            <Box sx={{ flex: 1, minWidth: 0, bgcolor: 'background.default', p: 2, borderRadius: 2, border: '1px solid #eee' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>사건 개요</Typography>
              <Box sx={{ mb: 3 }}>
                {brief.kor_summary ? (
                  <div
                    className="markdown-table"
                    dangerouslySetInnerHTML={{ __html: marked(brief.kor_summary || '', { breaks: true }) }}
                  />
                ) : (
                  <span style={{ color: '#aaa' }}>-</span>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>법률 분석</Typography>
              <Box>
                {brief.kor_content ? (
                  <div
                    className="markdown-table"
                    dangerouslySetInnerHTML={{ __html: marked(brief.kor_content || '', { breaks: true }) }}
                  />
                ) : (
                  <span style={{ color: '#aaa' }}>-</span>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {user && (
              <Button variant="contained" onClick={() => setEditOpen(true)}>
                수정
              </Button>
            )}
          </Box>
        </Paper>
        <CaseBriefForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSuccess={handleEditSuccess}
          initialData={brief}
        />
      </Box>
    </Container>
  );
};

export default CaseBriefDetail;
